import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import saveFile from 'helpers/saveFile';
import { FilterMatchMode } from 'primereact/api';
import { addLocale, locale } from 'primereact/api';
import {
  DataTable,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableRowClickEvent,
  DataTableRowReorderEvent,
  DataTableSelectionSingleChangeEvent,
  DataTableSortMeta,
  DataTableStateEvent
} from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { MultiSelectChangeEvent } from 'primereact/multiselect';
import { Toast } from 'primereact/toast';
import { Nullable } from 'primereact/ts-helpers';

import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import DeleteConfirmationDialog from 'ui/shared/DeleteConfirmationDialog';
import { ImportErrorDialog } from 'ui/shared/ImportErrorDialog';

import { AssetsService } from '../../components/services/AssetsService';
import { AssetTemplate, DROPDOWN_FILTER_DELAY } from '../../constants';
import { isSelected } from '../../helpers';
import { useDebounce } from '../../hooks';
import useAssetTypesService from '../../hooks/services/useAssetTypesService';
import { useApiCall } from '../../hooks/useApiCall';
import { useColumnSettings } from '../../hooks/useColumnSettings';
import { useColumnSettingsObserver } from '../../hooks/useColumnSettingsObserver';
import { ruLocale } from '../../lang/ruLocale';
import {
  Asset,
  BaseFilter,
  DropdownSelectorType,
  PageRequest
} from '../../types/interfaces';
import { AssetForEdit } from '../../types/interfaces/AssetForEdit';
import AssetCard from '../../ui/AssetCard/AssetCard';
import VisibilityWrapper from '../../utils/wrappers/VisibilityWrapper';

import AssetsTableHeader from './AssetsTableHeader';
import { getAssetColumns } from './getAssetColumns';
import '../../ui/css/Assets.css';

const storedAssetsFilter = 'storedAssetsFilter';

export default function Assets() {
  const assetsTypesDeletedId = '2713383261709';
  const [assets, setAssets] = useState<Asset[]>([]);
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [deleteDialogVisible, setDeleteDialogVisible] =
    useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState<{ error: string }[]>(
    []
  );
  const [errorDialogVisible, setErrorDialogVisible] = useState<boolean>(false);
  const [assetToDelete, setAssetToDelete] = useState<string | number | null>(
    null
  );
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const [selectedAssets, setSelectedAssets] = useState<Asset>(AssetTemplate);
  const [activeFilterActive, setActiveFilterActive] =
    useState<Nullable<boolean>>(true);
  const [filters, setFilters] = useState<
    Record<string, DataTableFilterMetaData>
  >({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    externalId: { value: null, matchMode: FilterMatchMode.CONTAINS },
    type: { value: null, matchMode: FilterMatchMode.CONTAINS },
    fullAddress: { value: null, matchMode: FilterMatchMode.CONTAINS },
    geographicalZone: { value: null, matchMode: FilterMatchMode.CONTAINS },
    state: { value: null, matchMode: FilterMatchMode.EQUALS },
    description: { value: null, matchMode: FilterMatchMode.CONTAINS }
  });
  const [assetTypesFilterOptions, setAssetTypesFilterOptions] = useState<
    DropdownSelectorType[]
  >([]);
  const [totalCount, setTotalCount] = useState(1);
  const [first, setFirst] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [multiSortMeta, setMultiSortMeta] = useState<
    DataTableSortMeta[] | undefined | null
  >(undefined);
  const onPageChanged = useCallback(async (event: DataTablePageEvent) => {
    if (event.rows !== undefined) {
      setFirst(0);
      setPageSize(event.rows);
    }
    if (event.page !== undefined) {
      setFirst(event.first);
      setPageNumber(event.first / event.rows + 1);
    }
  }, []);
  const setMultiSortMetaCallback = useCallback((e: DataTableStateEvent) => {
    setLoading(true);
    setMultiSortMeta(e.multiSortMeta);
  }, []);
  const onSelectionChangeCallback = useCallback(
    (e: DataTableSelectionSingleChangeEvent<Asset[]>) =>
      setSelectedAssets(e.value),
    []
  );
  const onRowDoubleClickCallback = useCallback((e: DataTableRowClickEvent) => {
    const asset = e.data as Asset;
    setSelectedAssets(asset);
    setDialogVisible(true);
  }, []);
  const onRowReorderCallback = useCallback(
    (e: DataTableRowReorderEvent<Asset[]>) => setAssets(e.value),
    []
  );

  const [selectedTypeFilter, setSelectedTypeFilter] = useState<
    DropdownSelectorType[]
  >([]);
  const [debouncedTypeFilter, setDebouncedTypeFilter] = useState<
    DropdownSelectorType[]
  >([]);
  const [hasHorizontalScroll, setHasHorizontalScroll] = useState(false);

  const assetsTableRef = useRef<HTMLDivElement>(null);

  const headerToFieldMap = useMemo(() => {
    const map = new Map<string, string>();
    map.set('Название объекта', 'name');
    map.set('Внешний номер', 'externalId');
    map.set('Тип объекта', 'type');
    map.set('Адрес', 'fullAddress');
    map.set('Географическая зона', 'geographicalZone');
    map.set('Активен', 'state');
    map.set('Описание', 'description');
    return map;
  }, []);

  const defaultOrder = [
    'name',
    'externalId',
    'type',
    'fullAddress',
    'geographicalZone',
    'state',
    'description'
  ];
  const defaultWidths = {
    name: '17%',
    externalId: '11%',
    type: '12%',
    fullAddress: '21%',
    geographicalZone: '14%',
    state: '5%',
    description: '20%'
  };

  // хук для управления настройками колонок
  const {
    columnOrder,
    columnWidths,
    handleColumnResizeEnd,
    updateColumnSettings
  } = useColumnSettings({
    orderStorageKey: 'assetsColumnOrder',
    widthsStorageKey: 'assetsColumnWidths',
    defaultOrder,
    defaultWidths,
    headerToFieldMap,
    tableRef: assetsTableRef,
    visibleColumns: undefined, // Assets не использует visibleColumns
    allFields: defaultOrder
  });

  const filtersForApi = useMemo((): PageRequest => {
    const sort = multiSortMeta?.find((x) => x !== undefined);
    const newFilters = { ...filters };
    newFilters.state = {
      value: activeFilterActive,
      matchMode: FilterMatchMode.EQUALS
    };
    const baseFilters = Object.keys(newFilters).map((x): BaseFilter => {
      const f = newFilters[x];
      return {
        field: x,
        value: (f as DataTableFilterMetaData).value,
        operator: (f as DataTableFilterMetaData).matchMode
      };
    });
    baseFilters.push({
      field: 'typeId',
      value: debouncedTypeFilter
        .map((x) => (x.id === assetsTypesDeletedId ? 'empty' : x.id ?? ''))
        .join(','),
      operator: FilterMatchMode.IN
    });
    return {
      pageNumber,
      pageSize,
      sortBy: sort?.field,
      sortDirection: sort?.order === 1 ? 'asc' : 'desc',
      filters: JSON.stringify(baseFilters)
    };
  }, [
    activeFilterActive,
    filters,
    multiSortMeta,
    pageNumber,
    pageSize,
    debouncedTypeFilter
  ]);

  useEffect(() => {
    localStorage.setItem(
      storedAssetsFilter,
      JSON.stringify(debouncedTypeFilter)
    );
  }, [debouncedTypeFilter]);

  const [loading, setLoading] = useState<boolean>(true);
  const assetNameFilterRef = useRef<HTMLInputElement>(null);
  const fullAddressFilterRef = useRef<HTMLInputElement>(null);
  const geographicalZoneFilterRef = useRef<HTMLInputElement>(null);
  const descriptionFilterRef = useRef<HTMLInputElement>(null);
  const externalIdFilterRef = useRef<HTMLInputElement>(null);
  const assetTypeFilterRef = useRef<HTMLInputElement>(null);
  const assetCardRef = useRef<null | { handleCancel: () => void }>(null);
  const toast = useRef<Toast>(null);
  const isInitialRender = useRef(true);
  const isSelectedIdExist = isSelected(selectedAssets);

  addLocale('ru', ruLocale.ru);
  locale('ru');

  const dFilter = useMemo(() => {
    return localStorage.getItem(storedAssetsFilter);
  }, []);

  const assetTypesService = useAssetTypesService();

  const getAssetTypes = useCallback(async () => {
    const warning = () =>
      toast.current &&
      toast.current.show({
        severity: 'warn',
        summary: 'Предупреждение',
        detail: 'Выберите тип объекта для отображения данных в таблице',
        life: 5000
      });

    const result = await assetTypesService.getForSelection()?.result;
    setAssetTypesFilterOptions(
      result
        ? [
            {
              id: assetsTypesDeletedId,
              label: 'Объекты с удаленным типом'
            },
            ...result
          ]
        : [{ id: assetsTypesDeletedId, label: 'Объекты с удаленным типом' }]
    );
    if (result && dFilter) {
      const parsedDFilter = (
        JSON.parse(dFilter) as DropdownSelectorType[]
      ).flatMap((x) => {
        if (x.id === assetsTypesDeletedId) return x;
        else {
          const department = result
            ? result.find((node) => node.id === x.id)
            : null;
          if (!department) return [];
          else if (department.label.localeCompare(x.label) !== 0) {
            return { ...x, label: department.label };
          }
          return x;
        }
      });
      setSelectedTypeFilter(parsedDFilter);
      setDebouncedTypeFilter(parsedDFilter);
      if (parsedDFilter.length === 0) warning();
    } else if (result && !dFilter) warning();
  }, [dFilter]);
  useApiCall(getAssetTypes);

  const getAssets = useCallback(async () => {
    if (debouncedTypeFilter.length < 1) {
      isInitialRender.current = false;
      setLoading(false);
      setAssets([]);
    } else {
      const result = await AssetsService.getAssets(filtersForApi);
      if (result.data) {
        setTotalCount((result.data.total === 0 ? 1 : result.data.total) ?? 1);
      }
      setAssets(result.data?.itemsList ?? []);
      setLoading(false);
    }
  }, [filtersForApi, debouncedTypeFilter.length]);
  useApiCall(getAssets);

  const onFiltersChange = useCallback((e: DataTableStateEvent) => {
    setPageNumber(1);
    setFirst(0);

    const next = e.filters as Record<string, DataTableFilterMetaData>;
    setFilters(next);
  }, []);

  const onFilterChange = (
    filter: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    const _filters = JSON.parse(JSON.stringify(filters));
    _filters[filter].value = value;
    setPageNumber(1);
    setFirst(0);
    setFilters(_filters);
  };

  const { debouncedCallback: debouncedOnFilterChange } = useDebounce(
    onFilterChange,
    300
  );

  const handleInputChange = useCallback(
    (filter: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedOnFilterChange(filter, e);
    },
    [debouncedOnFilterChange]
  );

  const exportTemplate = useCallback(async () => {
    const result = await AssetsService.getAssetsTemplateExcel();
    await saveFile(result.data, 'Шаблон.xls', '');
  }, []);

  const importAssetsXLS = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      // Validate the file
      const validationResponse = await AssetsService.importAssets(
        formData,
        true
      );
      if (validationResponse?.data?.errors?.length > 0) {
        setValidationErrors(
          validationResponse.data.errors.map((i) => ({ error: i }))
        );
        setErrorDialogVisible(true);
        return;
      }

      // Import the file (PUT)
      const importResponse = await AssetsService.importAssets(formData);
      toast.current?.show({
        severity: 'success',
        summary: 'Импорт успешно завершен',
        detail: `${importResponse.data.assets.length} объектов импортировано.`,
        sticky: true
      });

      // Optionally reload data or trigger a refresh
      await getAssets();
    } catch (error) {
      console.error('Ошибка импорта:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Ошибка импорта',
        detail: 'Произошла ошибка при импорте файла.',
        sticky: true
      });
    }
  };

  const exportExcel = useCallback(async () => {
    const result = await AssetsService.exportAssetsExcel(filtersForApi);
    await saveFile(result.data, 'Объекты.xls', '');
  }, [filtersForApi]);

  const handleDebouncedTypeFilterChange = (
    newSelectedTypeFilter: DropdownSelectorType[]
  ) => {
    setDebouncedTypeFilter(newSelectedTypeFilter);
  };

  // Wrap the debounced handler with useDebounce
  const { debouncedCallback: debouncedHandleTypeFilterChange } = useDebounce(
    handleDebouncedTypeFilterChange,
    DROPDOWN_FILTER_DELAY // Adjust the debounce delay as needed
  );

  const updateSelectedTypeFilter = (newFilter: DropdownSelectorType[]) => {
    setSelectedTypeFilter(newFilter);
    localStorage.setItem('storedAssetsFilter', JSON.stringify(newFilter));
    debouncedHandleTypeFilterChange(newFilter);
  };

  const handleMultiselectChange = (e: MultiSelectChangeEvent) => {
    const selectedValues = e.value as DropdownSelectorType[];
    if (selectedValues?.length === 0) setTotalCount(1);
    updateSelectedTypeFilter(selectedValues);
  };

  const handleDeleteTaskStatus = (taskId: string | number) => {
    const index = selectedTypeFilter.findIndex(
      (tagComponent) => tagComponent.id === taskId
    );

    if (index !== -1) {
      const updatedTaskStatuses = [
        ...selectedTypeFilter.slice(0, index),
        ...selectedTypeFilter.slice(index + 1)
      ];
      updateSelectedTypeFilter(updatedTaskStatuses);
    }
  };

  const checkHorizontalScroll = useCallback(() => {
    // Проверяем наличие горизонтального скролла у #table-header
    const tableHeader = document.querySelector('.assets-table #table-header');
    if (!tableHeader) {
      setHasHorizontalScroll(false);
      return;
    }

    // Проверяем наличие горизонтального скролла
    const hasHorizontal = tableHeader.scrollWidth > tableHeader.clientWidth;
    setHasHorizontalScroll(hasHorizontal);
  }, []);

  // хук для отслеживания изменений таблицы
  useColumnSettingsObserver({
    tableRef: assetsTableRef,
    updateColumnSettings,
    checkHorizontalScroll,
    dependencies: [assets]
  });

  const handleDeleteConfirmation = () => {
    handleDelete(assetToDelete);
    setDeleteDialogVisible(false);
  };

  const handleUpdate = async (
    id: string | number,
    updatedAsset: AssetForEdit
  ) => {
    const newAsset = { ...updatedAsset };
    newAsset.parameterValues = [];
    newAsset.parameters
      .filter((x) => x.selectedValue != null && x.selectedValue != -1)
      .forEach((x) => {
        newAsset.parameterValues.push({
          id: parseInt(x.id.toString()),
          value: {
            key: (x.values[x.selectedValue!] as any).id!.toString(),
            label: x.values[x.selectedValue!].label
          }
        });
      });
    (newAsset as any).parameters = undefined;
    const result = await AssetsService.updateAsset(Number(id), newAsset);
    await getAssets();
    return result.data;
  };

  const handleDelete = async (id: string | number | null) => {
    if (id != null)
      await AssetsService.deleteAsset(
        typeof id === 'string' ? parseInt(id) : id
      );
    await getAssets();
    setSelectedAssets(AssetTemplate);
  };

  const handleAdd = async (asset: AssetForEdit) => {
    const newAsset = { ...asset };
    newAsset.parameterValues = [];
    newAsset.parameters
      .filter((x) => x.selectedValue != null && x.selectedValue != -1)
      .forEach((x) => {
        newAsset.parameterValues.push({
          id: parseInt(x.id.toString()),
          value: {
            key: (x.values[x.selectedValue!] as any).id!.toString(),
            label: x.values[x.selectedValue!].label
          }
        });
      });
    (newAsset as any).parameters = undefined;
    const result = await AssetsService.createAsset(newAsset);
    await getAssets();
    return result.data;
  };

  const closeDialog = () => {
    setDialogVisible(false);
  };

  const onHideAssetCard = () => {
    if (assetCardRef.current !== null) {
      assetCardRef.current.handleCancel();
    }
  };

  const header = (
    <AssetsTableHeader
      selectedTypeFilter={selectedTypeFilter}
      assetTypesFilterOptions={assetTypesFilterOptions}
      handleMultiselectChange={handleMultiselectChange}
      activeFilterActive={activeFilterActive}
      setActiveFilterActive={setActiveFilterActive}
      handleInputChange={handleInputChange}
      handleDeleteTaskStatus={handleDeleteTaskStatus}
      exportTemplate={exportTemplate}
      importAssetsXLS={importAssetsXLS}
      exportExcel={exportExcel}
      setSelectedAssets={setSelectedAssets}
      setDialogVisible={setDialogVisible}
      toast={toast}
      isSelectedIdExist={isSelectedIdExist}
      setAssetToDelete={setAssetToDelete}
      setDeleteDialogVisible={setDeleteDialogVisible}
      selectedAssets={selectedAssets}
      hasHorizontalScroll={hasHorizontalScroll}
      isMobile={isMobile}
    />
  );

  const tableElement = document.querySelector('.p-datatable-thead');

  function offset(el: Element) {
    var rect = el.getBoundingClientRect(),
      scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return { top: rect.top + scrollTop };
  }

  const tableOffset = useMemo(() => {
    return tableElement instanceof HTMLElement ? offset(tableElement).top : 0;
  }, [assets]);

  const scrollHeight = useMemo(() => {
    const paginatorElement = document.querySelector('.p-paginator');
    const paginatorHeight =
      paginatorElement instanceof HTMLElement
        ? paginatorElement.offsetHeight
        : 0;
    return `calc(100dvh - ${
      tableElement instanceof HTMLElement ? tableOffset + paginatorHeight : 0
    }px)`;
  }, [tableElement, tableOffset]);

  const allColumns = useMemo(
    () =>
      getAssetColumns({
        filters,
        setFilters,
        setPageNumber,
        setFirst,
        handleInputChange,
        assetNameFilterRef,
        fullAddressFilterRef,
        geographicalZoneFilterRef,
        descriptionFilterRef,
        externalIdFilterRef,
        assetTypeFilterRef,
        columnOrder,
        columnWidths,
        activeFilterActive,
        setActiveFilterActive
      }),
    [
      columnOrder,
      columnWidths,
      filters,
      setFilters,
      setPageNumber,
      setFirst,
      handleInputChange,
      activeFilterActive,
      setActiveFilterActive
    ]
  );

  return (
    <div ref={assetsTableRef} className="assets-table wrapper wrapper--main">
      <Toast ref={toast} />

      <ImportErrorDialog
        errorDialogVisible={errorDialogVisible}
        setErrorDialogVisible={setErrorDialogVisible}
        validationErrors={validationErrors}
      />

      <Dialog
        header="Карточка объекта"
        visible={dialogVisible}
        style={{
          width: '743px',
          minHeight: 'fit-content',
          maxHeight: isMobile ? '96dvh' : 'auto'
        }}
        breakpoints={{ '960px': '75vw', '641px': '96vw' }}
        maximizable
        modal
        contentStyle={{ minHeight: 'fit-content' }}
        onHide={() => onHideAssetCard()}
      >
        <VisibilityWrapper code="asset-card__roles">
          <AssetCard
            assetProps={selectedAssets}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onAdd={handleAdd}
            closeDialog={closeDialog}
            ref={assetCardRef}
            isMobile={isMobile}
          />
        </VisibilityWrapper>
      </Dialog>

      <DeleteConfirmationDialog
        visible={deleteDialogVisible}
        onHide={() => setDeleteDialogVisible(false)}
        onConfirm={handleDeleteConfirmation}
        entityType="объект"
        entityName={selectedAssets?.name}
      />
      <DataTable
        filters={filters}
        paginator
        first={first}
        rows={pageSize}
        onPage={onPageChanged}
        totalRecords={totalCount}
        rowsPerPageOptions={[10, 25, 50, 100]}
        paginatorTemplate={
          isMobile
            ? 'RowsPerPageDropdown PrevPageLink CurrentPageReport NextPageLink'
            : 'RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink JumpToPageInput'
        }
        currentPageReportTemplate="{currentPage} из {totalPages}"
        paginatorLeft={' '}
        paginatorRight={`Всего: ${assets.length > 0 ? totalCount : 0}`}
        lazy
        multiSortMeta={multiSortMeta}
        onSort={setMultiSortMetaCallback}
        selectionMode="single"
        selection={selectedAssets!}
        onSelectionChange={onSelectionChangeCallback}
        onRowDoubleClick={onRowDoubleClickCallback}
        metaKeySelection={false}
        scrollable
        sortMode="multiple"
        removableSort
        scrollHeight={scrollHeight}
        size="small"
        resizableColumns
        reorderableColumns
        reorderableRows
        value={assets}
        dataKey="id"
        filterDisplay="row"
        loading={loading}
        header={header}
        emptyMessage="Объекты не найдены."
        onRowReorder={onRowReorderCallback}
        onColumnResizeEnd={handleColumnResizeEnd}
        tableStyle={{ minWidth: '50rem' }}
        onFilter={onFiltersChange}
      >
        {allColumns.map((col) => col.element)}
      </DataTable>
    </div>
  );
}
